
import { useState } from 'react';

interface Artwork {
  id: number;
  title: string;
  artist: string;
  technique: string;
  category: string;
  size: string;
  price: string;
  year: string;
  materials: string;
  description: string;
  image_url?: string;
  images?: string[];
  created_at: string;
}

interface ArtworkModalProps {
  artwork: Artwork | null;
  isOpen: boolean;
  onClose: () => void;
  onAddToCart: (id: number) => void;
  onToggleLike: (id: number) => void;
  isLiked: boolean;
}

const ArtworkModal = ({
  artwork,
  isOpen,
  onClose,
  onAddToCart,
  onToggleLike,
  isLiked,
}: ArtworkModalProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  // Fiyat formatlama fonksiyonu
  const formatPrice = (price: string | number) => {
    // Eğer zaten formatlanmışsa olduğu gibi döndür
    if (typeof price === 'string' && price.includes('TL+KDV')) {
      return price;
    }

    // Sayısal değeri al
    const numericPrice =
      typeof price === 'string' ? parseFloat(price.replace(/[^\d]/g, '')) : price;

    // Formatla ve TL+KDV ekle
    return `${numericPrice.toLocaleString('tr-TR')} TL+KDV`;
  };

  const handleAddToCart = async (id: number) => {
    setIsAddingToCart(true);
    
    // Sepete ekleme işlemi
    onAddToCart(id);
    
    // 2 saniye sonra modal'ı kapat ve durumu sıfırla
    setTimeout(() => {
      setIsAddingToCart(false);
      onClose();
    }, 2000);
  };

  if (!isOpen || !artwork) return null;

  // Tüm fotoğrafları al (images varsa onu kullan, yoksa image_url'i kullan)
  const allImages = artwork.images && artwork.images.length > 0 
    ? artwork.images 
    : artwork.image_url 
    ? [artwork.image_url] 
    : ["https://public.readdy.ai/ai/img_res/a1d524cbdf047ab6ba23c09bbf26a992.jpg"];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  const goToImage = (index: number) => {
    setCurrentImageIndex(index);
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-lg max-w-5xl w-full max-h-[85vh] overflow-hidden">
        <div className="flex flex-col lg:flex-row">
          {/* Image Section */}
          <div className="lg:w-3/5 relative">
            <div className="relative h-80 lg:h-[85vh]">
              <img
                src={allImages[currentImageIndex]}
                alt={`${artwork.title} - Fotoğraf ${currentImageIndex + 1}`}
                className="w-full h-full object-cover"
              />
              
              {/* Navigation Arrows */}
              {allImages.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/60 backdrop-blur-sm rounded-full text-white hover:bg-black/80 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-line text-lg"></i>
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/60 backdrop-blur-sm rounded-full text-white hover:bg-black/80 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-line text-lg"></i>
                  </button>
                </>
              )}

              {/* Image Counter */}
              {allImages.length > 1 && (
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black/70 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm">
                  {currentImageIndex + 1} / {allImages.length}
                </div>
              )}

              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-black/60 backdrop-blur-sm rounded-full text-white hover:bg-black/80 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Thumbnail Navigation */}
            {allImages.length > 1 && (
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex gap-2 justify-center overflow-x-auto pb-2">
                  {allImages.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => goToImage(index)}
                      className={`flex-shrink-0 w-12 h-8 rounded overflow-hidden border-2 transition-all cursor-pointer ${
                        index === currentImageIndex 
                          ? 'border-white shadow-lg' 
                          : 'border-transparent opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Content Section */}
          <div className="lg:w-2/5 p-6 overflow-y-auto max-h-[85vh]">
            <div className="space-y-4">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">{artwork.title}</h2>
                <p className="text-gray-300 text-lg">Sanatçı: {artwork.artist}</p>
                <p className="text-gray-400">Yıl: {artwork.year}</p>
              </div>

              <div className="flex items-center gap-3">
                <span className="bg-white/10 text-white px-3 py-1 rounded-full text-sm">
                  {artwork.technique}
                </span>
                <span className="text-gray-400 text-sm">{artwork.size}</span>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Malzemeler</h3>
                <p className="text-gray-300 text-sm">{artwork.materials}</p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Açıklama</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{artwork.description}</p>
              </div>

              <div className="border-t border-gray-700 pt-4">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-2xl font-bold text-white">{formatPrice(artwork.price)}</span>
                  <button
                    onClick={() => onToggleLike(artwork.id)}
                    className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                      isLiked
                        ? 'bg-red-500 text-white'
                        : 'bg-gray-800 text-gray-300 hover:text-red-400'
                    }`}
                  >
                    <i className={`ri-heart-${isLiked ? 'fill' : 'line'} text-lg`}></i>
                  </button>
                </div>

                <button
                  onClick={() => handleAddToCart(artwork.id)}
                  disabled={isAddingToCart}
                  className={`w-full py-3 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    isAddingToCart 
                      ? 'bg-green-500 text-white' 
                      : 'bg-white text-black hover:bg-gray-200'
                  }`}
                >
                  {isAddingToCart ? 'Eklendi!' : 'Sepete Ekle'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtworkModal;
